import React, { useState } from 'react';
import { UnreadMessagesModal } from './unread-messages-modal';
import { Tooltip, Skeleton, Button } from 'antd';
import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { AnalyticsCard } from './analytics-card';
import styles from './analytics-section.module.scss';
import { _getUnreadMessagesSPOT } from 'controllers/lead-assignments/lead-assignments-controller';

/**
 * Analytics data structure for the CRM system
 * @typedef {Object} AnalyticsData
 * @property {number} total - Total number of leads created on the range date
 * @property {number} admin_outside - Total number of leads created on the website and external forms on the range date
 * @property {number} admin_imported - Total number of leads imported on the range date
 * @property {number} admin_entered - Total number of leads entered on the range date
 * @property {number} unassigned_plan - Number of unassigned leads with plan
 * @property {number} unassigned_noplan - Number of unassigned leads without plan
 * @property {number} assigned_plan - Number of assigned leads with plan
 * @property {number} assigned_noplan - Number of assigned leads without plan
 * @property {number} contacted_plan - Number of contacted leads with plan (attempting-contact, working)
 * @property {number} contacted_noplan - Number of contacted leads without plan (attempting-contact, working)
 * @property {number} unread_email - Number of assigned leads without plan
 * @property {number} unread_sms - Number of assigned leads without plan
 */

/**
 * AnalyticsSection Component
 *
 * @component
 * @param {Object} props - Component props
 * @param {AnalyticsData} [props.analytics] - Analytics data object containing metrics
 * @param {AnalyticsData} [props.previousAnalytics] - Analytics data object containing metrics
 * @param {boolean} [props.loading=false] - Loading state indicator
 *
 * @returns {JSX.Element} Analytics section with 8 metric cards or loading skeletons
 *
 */
export const AnalyticsSectionV2 = ({ analytics, previousAnalytics, loading }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalData, setModalData] = useState([]);

  const handleShowUnreadMessages = async () => {
    setModalVisible(true);
    setModalLoading(true);
    setModalError(null);
    try {
      const response = await _getUnreadMessagesSPOT();
      const data = await response.json();
      setModalData(data);
    } catch (err) {
      setModalError(err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setModalData([]);
    setModalError(null);
  };
  if (loading) {
    return (
      <div className={styles.analyticsGrid}>
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} block active size="large" />
        ))}
      </div>
    );
  }

  const totalDiff = analytics?.total - (previousAnalytics?.total || 0);
  const admin_outsideDiff = analytics?.admin_outside - (previousAnalytics?.admin_outside || 0);
  const admin_importedDiff = analytics?.admin_imported - (previousAnalytics?.admin_imported || 0);
  const admin_enteredDiff = analytics?.admin_entered - (previousAnalytics?.admin_entered || 0);

  return (
    <div className={styles.analyticsGrid}>
      {/* Total Leads Card - Shows the complete count of leads in the system */}
      <div style={{ height: '100%' }}>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              Total Leads
              <Tooltip
                title={
                  <span>
                    Total number of leads currently in the system
                    <br />
                    {analytics?.date || ''} vs
                    <br />
                    {previousAnalytics?.date || ''}
                  </span>
                }
                getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </div>
          }
          value={analytics?.total || 0}
          description={(() => {
            const diff = totalDiff || 0;
            let icon;
            let color;
            if (diff > 0) {
              icon = '▲';
              color = '#52c41a';
            } else if (diff < 0) {
              icon = '▼';
              color = '#ff4d4f';
            } else {
              icon = '–';
              color = '#bfbfbf';
            }
            return `
                 <div style="display: flex; align-items: center; justify-content: center; flex-direction: column;">
                   <span>
                     <span style="color: ${color}; font-size: 12px; margin-right: 4px;">${icon}</span>
                     <span style="color: ${color}; font-weight: 700;">${Math.abs(diff)}</span>
                   </span>
                   <span>Total leads</span>
                 </div>
               `;
          })()}
        />
      </div>

      <div>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              External Lead Uploads
              <Tooltip
                title={
                  <span>
                    Leads that haven't been assigned to any team member yet
                    <br />
                    {analytics?.date || ''} vs
                    <br />
                    {previousAnalytics?.date || ''}
                  </span>
                }
                getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </div>
          }
          value={analytics?.admin_imported + analytics?.admin_entered || 0}
          description={(() => {
            const diff = admin_importedDiff + admin_enteredDiff || 0;
            let icon;
            let color;
            if (diff > 0) {
              icon = '▲';
              color = '#52c41a';
            } else if (diff < 0) {
              icon = '▼';
              color = '#ff4d4f';
            } else {
              icon = '–';
              color = '#bfbfbf';
            }
            return `
                <div style="display: flex; align-items: center; justify-content: center; flex-direction: column;">
                  <span>
                    <span style="color: ${color}; font-size: 12px; margin-right: 4px;">${icon}</span>
                    <span style="color: ${color}; font-weight: 700;">${Math.abs(diff)}</span>
                  </span>
                  <span>Leads Imported or entered manually on the RM</span>
                </div>
              `;
          })()}
        />
      </div>

      <div>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              Schola Leads
              <Tooltip
                title={
                  <span>
                    Leads from the website and external forms
                    <br />
                    {analytics?.date || ''} vs
                    <br />
                    {previousAnalytics?.date || ''}
                  </span>
                }
                getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </div>
          }
          value={analytics?.admin_outside || 0}
          description={(() => {
            const diff = admin_outsideDiff || 0;
            let icon;
            let color;
            if (diff > 0) {
              icon = '▲';
              color = '#52c41a';
            } else if (diff < 0) {
              icon = '▼';
              color = '#ff4d4f';
            } else {
              icon = '–';
              color = '#bfbfbf';
            }
            return `
                <div style="display: flex; align-items: center; justify-content: center; flex-direction: column;">
                  <span>
                    <span style="color: ${color}; font-size: 12px; margin-right: 4px;">${icon}</span>
                    <span style="color: ${color}; font-weight: 700;">${Math.abs(diff)}</span>
                  </span>
                  <span>Leads from the website and external forms</span>
                </div>
              `;
          })()}
        />
      </div>

      <div>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              Unassigned
              <Tooltip
                title="Leads that haven't been assigned to any team member yet"
                getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </div>
          }
          value={analytics?.unassigned_plan + analytics?.unassigned_noplan || 0}
          description="Awaiting assignment"
        />
      </div>

      <div>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              Working Assigned
              <Tooltip title="Leads currently assigned to team members" getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </div>
          }
          value={analytics?.assigned_plan + analytics?.assigned_noplan || 0}
          description={`<div style="display: flex; justify-content: center; align-items: center; font-size: 10px;">(Basic)<b style="color: #000000; font-weight: 700;">${analytics?.assigned_noplan}&nbsp;&nbsp;/&nbsp;&nbsp;${analytics?.assigned_plan}</b>(Partner)</div>
              <div>Currently being worked on.</div>`}
        />
      </div>

      <div>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              Contacted
              <Tooltip title="Contacted/WIP" getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </div>
          }
          value={`${analytics?.contacted_noplan + analytics?.contacted_plan || 0}`}
          description={`Contacted/WIP leads`}
        />
      </div>

      <div>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              Reach
              <Tooltip
                title="Percentage of Contacted/WIP leads out of the assigned leads"
                getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            </div>
          }
          value={(() => {
            const assigned = (analytics?.assigned_noplan || 0) + (analytics?.assigned_plan || 0);
            const contacted = (analytics?.contacted_noplan || 0) + (analytics?.contacted_plan || 0);
            const percent = assigned > 0 ? Math.round((contacted / assigned) * 100) : 0;
            return `${percent}%`;
          })()}
          description="Contacted/WIP leads out of the assigned leads"
        />
      </div>

      <div>
        <AnalyticsCard
          title={
            <div className={styles.cardTitle}>
              Unread Messages
              <Tooltip
                title="Number of Unread Messages of Active Clients"
                getPopupContainer={(trigger) => document.body}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
              {analytics?.unread_email + analytics?.unread_sms > 0 && (
                <span className={styles.searchIconWrapper}>
                  <SearchOutlined
                    style={{ marginLeft: 8, fontSize: 18, cursor: 'pointer' }}
                    title="View detail"
                    onClick={handleShowUnreadMessages}
                  />
                </span>
              )}
            </div>
          }
          value={`${analytics?.unread_email + analytics?.unread_sms || 0}`}
          description={`<div style="display: flex; justify-content: center; align-items: center; font-size: 10px;">(Email)<b style="color: #000000; font-weight: 700;">${analytics?.unread_email}&nbsp;&nbsp;/&nbsp;&nbsp;${analytics?.unread_sms}</b>(Sms)</div>
              <div>Unread Messages of Active Clients</div>`}
        />
        <UnreadMessagesModal
          visible={modalVisible}
          onClose={handleCloseModal}
          loading={modalLoading}
          error={modalError}
          data={modalData}
        />
      </div>
    </div>
  );
};
